import React from 'react';
import { useLanguage } from '../../hooks/useLanguage';

const RecentActivityTable = ({ data, loading }) => {
    const { t, currentLanguage } = useLanguage();
    
    // Función para formatear fecha relativa según idioma usando la fecha ISO del backend
    const formatRelativeDate = (dateString) => {
        if (!dateString) return '-';
        
        try {
            // Intentar parsear como fecha ISO
            const date = new Date(dateString);
            
            // Verificar si es una fecha válida
            if (isNaN(date.getTime())) {
                // Si no es fecha ISO, intentar traducir el formato relativo en español
                return translateRelativeDate(dateString);
            }
            
            // Calcular diferencia con ahora
            const now = new Date();
            const diffMs = now - date;
            const diffSecs = Math.floor(diffMs / 1000);
            const diffMins = Math.floor(diffSecs / 60);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);
            const diffWeeks = Math.floor(diffDays / 7);
            const diffMonths = Math.floor(diffDays / 30);
            const diffYears = Math.floor(diffDays / 365);
            
            // Formato relativo según idioma
            if (currentLanguage === 'en') {
                if (diffSecs < 60) return 'A few seconds ago';
                if (diffMins === 1) return '1 min ago';
                if (diffMins < 60) return `${diffMins} mins ago`;
                if (diffHours === 1) return '1 hour ago';
                if (diffHours < 24) return `${diffHours} hours ago`;
                if (diffDays === 1) return 'Yesterday';
                if (diffDays < 7) return `${diffDays} days ago`;
                if (diffWeeks === 1) return '1 week ago';
                if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
                if (diffMonths === 1) return '1 month ago';
                if (diffMonths < 12) return `${diffMonths} months ago`;
                if (diffYears === 1) return '1 year ago';
                return `${diffYears} years ago`;
            } else {
                // Español
                if (diffSecs < 60) return 'Hace unos segundos';
                if (diffMins === 1) return 'Hace 1 min';
                if (diffMins < 60) return `Hace ${diffMins} mins`;
                if (diffHours === 1) return 'Hace 1 hora';
                if (diffHours < 24) return `Hace ${diffHours} horas`;
                if (diffDays === 1) return 'Ayer';
                if (diffDays < 7) return `Hace ${diffDays} días`;
                if (diffWeeks === 1) return 'Hace 1 semana';
                if (diffWeeks < 4) return `Hace ${diffWeeks} semanas`;
                if (diffMonths === 1) return 'Hace 1 mes';
                if (diffMonths < 12) return `Hace ${diffMonths} meses`;
                if (diffYears === 1) return 'Hace 1 año';
                return `Hace ${diffYears} años`;
            }
        } catch (e) {
            // Fallback: traducir formato relativo en español si falla el parsing
            return translateRelativeDate(dateString);
        }
    };
    
    // Función de fallback para traducir formato relativo en español (compatibilidad)
    const translateRelativeDate = (dateString) => {
        if (currentLanguage === 'en') {
            let result = dateString;
            
            // Traducciones específicas
            if (result === 'Hace unos segundos') return 'A few seconds ago';
            if (result === 'Ayer') return 'Yesterday';
            if (result === 'Nunca') return 'Never';
            
            // Reemplazar "Hace" por vacío y agregar "ago" al final
            if (result.startsWith('Hace ')) {
                result = result.replace('Hace ', '');
                
                // Traducir unidades
                result = result.replace(' hora', ' hour');
                result = result.replace(' horas', ' hours');
                result = result.replace(' días', ' days');
                result = result.replace(' día', ' day');
                result = result.replace(' semana', ' week');
                result = result.replace(' semanas', ' weeks');
                result = result.replace(' mes', ' month');
                result = result.replace(' meses', ' months');
                result = result.replace(' año', ' year');
                result = result.replace(' años', ' years');
                
                return result + ' ago';
            }
        }
        
        return dateString;
    };
    
    if (loading) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <h5 className="card-title mb-4">{t('metrics.recentActivity.title')}</h5>
                    <div className="placeholder-glow">
                        {[1,2,3,4,5,6,7,8].map(i => (
                            <div key={i} className="placeholder col-12 mb-2" style={{height: '35px'}}></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <h5 className="card-title mb-4">{t('metrics.recentActivity.title')}</h5>
                    <div className="text-center text-muted py-5">
                        <i className="bi bi-clock-history" style={{fontSize: '3rem'}}></i>
                        <p className="mt-3">{t('metrics.recentActivity.noActivity')}</p>
                    </div>
                </div>
            </div>
        );
    }

    const getModuloBadgeClass = (modulo) => {
        return modulo === 'Policy Navigator' ? 'bg-primary' : 'bg-success';
    };

    const getEstadoIcon = (estado) => {
        if (estado === 'exitoso') return <i className="bi bi-check-circle-fill text-success"></i>;
        if (estado === 'error') return <i className="bi bi-x-circle-fill text-danger"></i>;
        return <i className="bi bi-dash-circle text-muted"></i>;
    };

    const truncateText = (text, maxLength = 50) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-body">
                <h5 className="card-title mb-4">
                    <i className="bi bi-clock-history me-2 text-info"></i>
                    {t('metrics.recentActivity.title')} ({t('metrics.recentActivity.subtitle')})
                </h5>
                
                <div className="table-responsive" style={{maxHeight: '600px', overflowY: 'auto'}}>
                    <table className="table table-sm table-hover align-middle">
                        <thead className="table-light sticky-top">
                            <tr>
                                <th style={{width: '60px'}}></th>
                                <th style={{width: '140px'}}>{t('metrics.recentActivity.date')}</th>
                                <th style={{width: '180px'}}>{t('metrics.recentActivity.user')}</th>
                                <th style={{width: '130px'}}>{t('metrics.recentActivity.module')}</th>
                                <th>{t('metrics.recentActivity.query')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((activity, index) => (
                                <tr key={index}>
                                    <td className="text-center">
                                        {getEstadoIcon(activity.estado)}
                                    </td>
                                    <td className="text-muted small">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        {formatRelativeDate(activity.fecha)}
                                    </td>
                                    <td className="font-monospace small text-truncate" style={{maxWidth: '180px'}}>
                                        <i className="bi bi-person-circle me-1"></i>
                                        {activity.usuario}
                                    </td>
                                    <td>
                                        <span className={`badge ${getModuloBadgeClass(activity.modulo)}`}>
                                            {activity.modulo}
                                        </span>
                                    </td>
                                    <td>
                                        <span 
                                            className="text-dark" 
                                            title={activity.titulo}
                                            style={{cursor: 'help'}}
                                        >
                                            {truncateText(activity.titulo, 60)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                <div className="mt-3 text-end">
                    <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        {t('metrics.recentActivity.showing', { count: data.length })}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default RecentActivityTable;
